import { motion } from 'framer-motion';
import { ArrowRight, Car, Coffee, Info, Wifi, Eye } from 'lucide-react';
import { useState } from 'react';
import { usePaymentAccess } from '../../../../../hooks/usePaymentAccess';
import { richTextToPlainText } from '../../../../../utils/richText';

const WorkspaceCard = ({
  title,
  description,
  icon,
  imageURL,
  onBook,
  index,
  price
}) => {
  const [expanded, setExpanded] = useState(false);
  const { isPaymentAllowed } = usePaymentAccess();
  // Descriptions are authored as rich text, so render the readable text only.
  const descriptionText = richTextToPlainText(description);

  // Only change the text and icon based on payment access
  const buttonText = isPaymentAllowed ? "Book This Space" : "View Space";
  const ButtonIcon = isPaymentAllowed ? ArrowRight : Eye;

  const handleButtonClick = () => {
    onBook({ title, description, price }); // Same function call, same behavior
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      whileHover={{ y: -4 }}
      className="group bg-white border border-slate-200 hover:border-blue-200 hover:shadow-lg overflow-hidden"
      style={{ borderRadius: '16px' }}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden bg-slate-100">
        {imageURL ? (
          <>
            <img
              src={imageURL}
              alt={title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute top-4 right-4 bg-slate-800/80 px-3 py-1.5 flex items-center gap-1.5 rounded-lg">
              <Info className="w-3 h-3 text-slate-300" />
              <span className="text-xs text-slate-200">
                Illustrative purposes only
              </span>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            {icon}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-800">
            {title}
          </h3>
          {price && (
            <div className="text-right">
              <span className="text-lg font-bold">${price}</span>
              <span className="text-sm text-slate-500">/hour</span>
            </div>
          )}
        </div>

        {/* Description */}
        <p
          className={`text-sm text-slate-600 leading-relaxed ${
            expanded ? '' : 'line-clamp-2'
          }`}
        >
          {descriptionText}
        </p>

        {descriptionText.length > 120 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-blue-600 font-medium mt-2 hover:underline"
          >
            {expanded ? 'Show less' : 'Read more'}
          </button>
        )}

        {/* Amenities */}
        <div className="flex gap-5 my-6 text-xs font-medium text-slate-500">
          <span className="flex items-center">
            <Wifi className="w-3.5 h-3.5 mr-1.5 text-emerald-500" /> WiFi
          </span>
          <span className="flex items-center">
            <Coffee className="w-3.5 h-3.5 mr-1.5 text-amber-500" /> Coffee
          </span>
          <span className="flex items-center">
            <Car className="w-3.5 h-3.5 mr-1.5 text-blue-500" /> Parking
          </span>
        </div>

        {/* Button - Same behavior, just text and icon change */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleButtonClick}
          className="w-full bg-blue-600 text-white py-3 font-semibold rounded-xl flex items-center justify-center"
        >
          {buttonText}
          <ButtonIcon className="ml-2 w-4 h-4" />
        </motion.button>
      </div>
    </motion.div>
  );
};

export default WorkspaceCard;